import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { ArrowLeft, Plus, Flag, Loader2, ClipboardSignature, X, Link2 } from "lucide-react";
import { handoverApi } from "./handover/handoverApi";
import { useDefectOptions } from "./handover/useDefectOptions";
import { WalkthroughItemDialog } from "./handover/WalkthroughItemDialog";
import { WalkthroughItemCard } from "./handover/WalkthroughItemCard";
import { FinishWalkthroughDialog } from "./handover/FinishWalkthroughDialog";
import { WalkthroughRecord } from "./handover/WalkthroughRecord";
import { fmtDateTime } from "./handover/imageUtils";
import { ShareHandoverLinkDialog } from "./handover/ShareHandoverLinkDialog";

export default function WalkthroughPage() {
  const { walkthroughId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = () => (location.key === "default" ? navigate("/defects/client-walkthroughs") : navigate(-1));
  const [wt, setWt] = useState(null);
  const [adding, setAdding] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const closeShare = useCallback((o) => !o && setSharing(false), []);
  const shareDwelling = useMemo(
    () => (sharing && wt ? { id: wt.dwelling_id, dwelling_number: wt.dwelling_number } : null),
    [sharing, wt?.dwelling_id, wt?.dwelling_number]);
  const options = useDefectOptions(wt?.site_id);

  const load = useCallback(() => handoverApi(`/walkthroughs/${walkthroughId}`).then(setWt).catch((e) => toast.error(e.message)), [walkthroughId]);
  useEffect(() => { load(); }, [load]);

  const removeItem = async (item) => {
    if (!window.confirm(`Remove "${item.area} — ${item.element}: ${item.issue}" from this walkthrough? The defect will be deleted.`)) return;
    try {
      await handoverApi(`/walkthroughs/${walkthroughId}/items/${item.id}`, { method: "DELETE" });
      toast.success("Item removed", { position: "top-center" });
      load();
    } catch (e) { toast.error(e.message); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this walkthrough? Nothing has been recorded yet.")) return;
    try {
      await handoverApi(`/walkthroughs/${walkthroughId}`, { method: "DELETE" });
      toast.success("Walkthrough cancelled");
      goBack();
    } catch (e) { toast.error(e.message); }
  };

  if (!wt) return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-600" /></div>;
  const inProgress = wt.status === "in_progress";
  const client = wt.client || {};

  return (
    <div className="p-4 sm:p-8 max-w-3xl mx-auto space-y-5 pb-28" data-testid="walkthrough-page">
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" onClick={goBack} className="-ml-2 text-slate-600" data-testid="walkthrough-back-button">
          <ArrowLeft className="w-4 h-4 mr-1" />Back
        </Button>
        <Button variant="outline" onClick={() => setSharing(true)} className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="walkthrough-share-link-button">
          <Link2 className="w-4 h-4 mr-1.5" />Share Handover Link
        </Button>
      </div>
      <div>
        <p className="text-sm font-semibold uppercase tracking-wide text-amber-700 flex items-center gap-1.5">
          <ClipboardSignature className="w-4 h-4" />Client Walkthrough #{wt.sequence}
        </p>
        <h1 className="heading-primary text-3xl sm:text-4xl" data-testid="walkthrough-dwelling">{wt.dwelling_number}</h1>
        <p className="text-slate-600" data-testid="walkthrough-client">
          {client.company_name ? `${client.company_name} — ` : ""}{client.name} · {client.client_type === "developer" ? "Developer" : "Purchaser"} · started {fmtDateTime(wt.started_at)}
        </p>
      </div>

      {inProgress ? (
        <>
          <div className="rounded-xl bg-amber-100 border border-amber-300 px-4 py-3 text-amber-900 text-sm font-medium" data-testid="walkthrough-in-progress-banner">
            Walkthrough in progress — every item recorded here is shared with the client.
          </div>
          <div className="space-y-2" data-testid="walkthrough-items">
            {wt.items.length === 0 ? (
              <p className="text-slate-500 py-6 text-center" data-testid="walkthrough-empty">No items yet. Tap “Record Item” as you walk through with the client.</p>
            ) : wt.items.map((it, i) => <WalkthroughItemCard key={it.id} item={it} index={i} onRemove={removeItem} />)}
          </div>
          {wt.items.length === 0 && (
            <Button variant="ghost" onClick={cancel} className="text-slate-500" data-testid="walkthrough-cancel-button"><X className="w-4 h-4 mr-1" />Cancel walkthrough</Button>
          )}
          <div className="fixed bottom-0 inset-x-0 z-30 bg-white/90 backdrop-blur border-t border-slate-200 p-3">
            <div className="max-w-3xl mx-auto flex gap-2">
              <Button onClick={() => setAdding(true)} className="flex-1 btn-construction h-12 text-base" data-testid="walkthrough-add-item-button">
                <Plus className="w-5 h-5 mr-1" />Record Item
              </Button>
              <Button onClick={() => setFinishing(true)} disabled={!wt.items.length} variant="outline" className="flex-1 h-12 text-base border-slate-300" data-testid="walkthrough-finish-button">
                <Flag className="w-5 h-5 mr-1" />Finish ({wt.items.length})
              </Button>
            </div>
          </div>
          <WalkthroughItemDialog open={adding} onOpenChange={setAdding} walkthroughId={walkthroughId} siteName={wt.site_name} dwellingNumber={wt.dwelling_number} options={options} onAdded={load} />
          <FinishWalkthroughDialog open={finishing} onOpenChange={setFinishing} walkthrough={wt} itemCount={wt.items.length} onFinished={(rec) => { setWt(rec); setSharing(true); }} />
        </>
      ) : (
        <WalkthroughRecord wt={wt} />
      )}
      <ShareHandoverLinkDialog dwelling={shareDwelling} onOpenChange={closeShare} />
    </div>
  );
}
